import React, { useState } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import styled from 'styled-components';

import { useTranslations } from '@lang';
import { RHFBlockInputField } from '@common/components/FormElements/BlockInputField';
import { Spinner } from '@common/components/Spinner';
import { FormRow, FormSuccessMessage } from '@common/components/AuthLayout/Layout';
import { Button } from '@common/components';
import Error from '@common/components/Error';
import { Link } from 'shared-routing';
import { Routes } from '@common/constants';
import { EMAIL_REGEXP } from '@utils/validators';

const forgottenPasswordSchema = z.object({
  email: z.string().min(1, 'required').regex(EMAIL_REGEXP, 'email'),
});

type ForgottenPasswordFormValues = z.infer<typeof forgottenPasswordSchema>;

type ForgottenPasswordFormProps = {
  onSubmit: (data: ForgottenPasswordFormValues) => void;
};

const SubmitButton = styled(Button)`
  margin-top: 15px;
`;

const ForgottenPasswordForm = ({ onSubmit }: ForgottenPasswordFormProps) => {
  const { t } = useTranslations();
  const [submitError, setSubmitError] = useState('');
  const [isSubmitSuccessful, setIsSubmitSuccessful] = useState(false);

  const {
    handleSubmit,
    control,
    formState: { isSubmitting },
  } = useForm<ForgottenPasswordFormValues>({
    resolver: zodResolver(forgottenPasswordSchema),
    defaultValues: { email: '' },
    mode: 'onChange',
  });

  const handleFormSubmit = async (data: ForgottenPasswordFormValues) => {
    setSubmitError('');
    try {
      await onSubmit(data);
      setIsSubmitSuccessful(true);
    } catch (e) {
      setSubmitError('resetPasswordFailed');
    }
  };

  return (
    <form onSubmit={handleSubmit(handleFormSubmit)} noValidate>
      <Spinner isActive={isSubmitting} showDelayMessage />
      {!isSubmitSuccessful && (
        <>
          <FormRow>
            <RHFBlockInputField<ForgottenPasswordFormValues>
              name="email"
              control={control}
              label={t('forgottenPassword.emailLabel')}
            />
          </FormRow>
          <FormRow align="right" actionButtons>
            <Link route={Routes.SIGNIN}>
              <Button type="button" isNarrow isInverted disabled={isSubmitting}>
                {t('forgottenPassword.backToLogin')}
              </Button>
            </Link>{' '}
            <SubmitButton type="submit" isNarrow disabled={isSubmitting} isPrimary>
              {t('forgottenPassword.send')}
            </SubmitButton>
          </FormRow>
          {submitError && <Error error={{ code: submitError }} />}
        </>
      )}
      {isSubmitSuccessful && (
        <>
          <FormRow>
            <FormSuccessMessage>{t('forgottenPassword.successMessage')}</FormSuccessMessage>
          </FormRow>
          <FormRow align="right">
            <Link route={Routes.HOME}>
              <Button type="button" isNarrow isInverted disabled={isSubmitting}>
                {t('forgottenPassword.backToHomepage')}
              </Button>
            </Link>
          </FormRow>
        </>
      )}
    </form>
  );
};

export default ForgottenPasswordForm;
